import React, { Component } from 'react';
import styled, { keyframes } from 'styled-components';
import {B64Image} from '../components/B64Image';
import { motion } from 'framer-motion';
import {i18n} from '../i18n';
import {IconButton} from './Buttons';

const config = window.dodConfig;
export const hideScrollbar = `
	scrollbar-width: none;
	-webkit-overflow-scrolling: touch;
	&::-webkit-scrollbar {
		width: 0 !important;
		height: 0 !important;
	}
`;

export const Bkg = styled(B64Image)`
	position: absolute;
	top: ${p => p.t || 0}vw; 
	left: ${p => p.l || 0}vw;
	width: 100%; 
	height: 100%;
`;


const ButtonBarInner = styled.div`
	padding: 2% 0;
	width: 100%;
	flex-basis: 0;
	display: flex;
	justify-content: space-evenly;
	& .sprite {
		height: 15.5vw;
	}
	@media (min-aspect-ratio: 9/15) {
		& .sprite {
			height: 14vw;
		}
	}
`;
export class ButtonBar extends Component {
	render() {
		return (
			<ButtonBarInner className={`button-bar ${this.props.className}`} 
				style={this.props.style} 
				rightAlign={this.props.rightAlign}>
				{this.props.children}
			</ButtonBarInner>
		);
	}
}

const rotate = keyframes`
	0% {
		transform: rotate(0deg);
	}

	50% {
		transform: rotate(180deg);
	}

	100% {
		transform: rotate(360deg);
	}
`;

const SpinnerPositioner = styled(motion.div)`
	position: absolute;
	top: 0;
	left: 0;
	height: 50vh;
	width: 100vw;
	display: flex;
	justify-content: center;
	align-items: center;
	pointer-events: none;
	z-index: 10000;
`;
const SpinnerBody = styled.div`
	width: ${(p) => (config.isLandscape ? 18 : 25)}%;
	position: relative;
`;
const Inner = styled(B64Image)`
	width: 100%;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 2;
`;
const Outer = styled(B64Image)`
	width: 100%;
	position: absolute;
	animation: ${rotate} 800ms linear infinite;
	top: 0;
	left: 0;
	z-index: 1;
`;
export class Spinner extends Component {
	render() {
		const variants = {
			visible: { opacity: 1, transition: { delay: 0.25, duration: 1 } },
			hidden: { opacity: 0, transition: { delay: 0, duration: 0.1 } },
		};
		return (
			<SpinnerPositioner initial="hidden" animate="visible" exit={'hidden'} variants={variants}>
				<SpinnerBody>
					<Outer src="/assets/spinner_ring.png" />
					<Inner src="/assets/spinner_icon.png" />
				</SpinnerBody>
			</SpinnerPositioner>
		);
	}
}

export const stepShadow = (steps, width, color) => {
	let css = "";
	for (let i = 0; i < steps; i++) {
	  let angle = (i * 2 * Math.PI) / steps;
	  let cos = Math.round(10000 * Math.cos(angle)) / 10000;
	  let sin = Math.round(10000 * Math.sin(angle)) / 10000;
	  css += `calc(${cos} * ${width}) calc(${sin} * ${width}) 0 ${color}`;
	  if(i !== steps-1) css += ','
	}
	return css;
};
export const StrokeText = styled.span`
	text-shadow: ${p => stepShadow(16, p.stroke, p.color) + (p.add ? ', '+p.add : '')};
}`

export const Question = styled(p => {return (
	<div className={p.className}>
		<IconButton size={7}
			src="/assets/buttons/btn_help.png"
			onClick={p.onClick}
		/>
	</div>
)})`
	position:absolute;
	top: 5vw;
	right: ${p => p.r || 10}vw;
`;

export const ScrollContainer = styled(motion.div)`
	background: url(${p => p.bkg}) center/100% 100% no-repeat;
	width: ${p => p.w}vw;
	flex: 1 0 30vh;
	position: relative;
	overflow: hidden;

	::after {
		content: '';
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		background: url(${p => p.overlay}) center/100% 100% no-repeat;
		pointer-events: none;
	}
`;
export const Scroller = styled(motion.div)`
	padding: 0.59vh 0;
	height: 100%;
	display: flex;
	flex-direction: column;
	-webkit-overflow-scrolling: touch;
	overflow-y: scroll;
	${hideScrollbar};
`;
export const ScrollArrow = styled(p => {return (
	<motion.div className = {p.className}
		initial={{bottom: '3vw', opacity: 0}}
		animate={{bottom: ['0vw', '-3vw', '0vw'], opacity: 1}}
		transition={{
			bottom: {
				type: 'tween', ease: 'easeInOut', 
				duration: 2, loop: Infinity,
				times: [0, 0.5, 1]
			},
			opacity: {duration: 0.5}
		}}
		exit={{opacity: 0, duration: 0.5}}
	>
		<B64Image src="/assets/arrow_white.png"/>
	</motion.div>
)})`
	position:absolute;
	width:100%;
	z-index: 2;
	bottom: 0vw;

	img { width: 15vw; }
`;

const stringToTags = str => {
	const split = str.match(/(<\w+>)?(\w|\s|([.,!?:;'"'\-()™+%&*])|[\u00c0-\u00dd]|\u00a1|\u00bf)+(<\/\w+>)?/gi);
	let i = 0;
	const tags = split.map(ch => {
		const hasTag = ch.indexOf('<') === 0;
		const ind = ch.indexOf('>');
		const tName = ch.slice(1, ind);
		const content = ch.slice(ind+1, ch.indexOf('<', ind));
		const CustomTag = `${tName}`
		const obj = <CustomTag key={tName+tName + i++}>{content}</CustomTag>
		return hasTag ? obj : ch;
	});	
	//console.log('OUTLINE :: str =', str, 'spl =', split, 't =', tags)
	return tags		
}
export const Outline = styled(p => { 
	return (
	<div className = {p.className}>
		<StrokeText color={p.color || '#792c02'} stroke={(p.stroke || 0.5) + "vw"}>
			{p.children ? typeof(p.children) === 'string' ? stringToTags(p.children)
			 : p.children.map(ch => typeof(ch) === 'string' ? stringToTags(ch) : ch) 
			: i18n.exists(p.textKey) ? stringToTags(i18n.t(p.textKey)) : p.textKey}
		</StrokeText>
	</div>
)})`
	b { font-weight: bolder; }
`;

export const Badge = styled(p => { 
	return !p.num ? null : (
	<div className={p.className}>
		<Bkg src="/assets/btn_nav_new_ach.png" className='badgeBkg'/>
		<div>{p.num}</div>
	</div>
)})`
	position: absolute;
	top: ${p => p.top || -3}vw;
	right: ${p => p.right || 0}vw;
	width: ${p => p.size || 9}vw;
	height: ${p => p.size || 9}vw;
	display: flex;
    justify-content: center;
    align-items: center;

	div {
		position: relative;
		text-shadow: 0 0.46vw 0 #00000066;
		font-weight: 900;
		font-size: ${p => p.size/2 || 4.5}vw;
		top: ${p => p.t || -0.5}vw;
	}
`;
